import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { count, flushQueue } from "@/lib/offlineQueue";
import { autoPreloadIfStale } from "@/lib/offlinePreload";
import { toast } from "sonner";
import { CloudArrowUp, WifiSlash, CheckCircle } from "@phosphor-icons/react";

/**
 * Barra flutuante que:
 *  - Mostra "N apontamentos aguardando envio" quando a fila offline tem itens
 *  - Botão de sincronizar manual
 *  - Auto-sincroniza quando o navegador dispara 'online'
 *  - Notifica quando terminar
 */
export default function OfflineSync() {
  const navigate = useNavigate();
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);

  const refresh = useCallback(async () => {
    setPending(await count());
  }, []);

  const sync = useCallback(async (silent = false) => {
    if ((await count()) === 0) {
      if (!silent) toast.info("Nenhum apontamento pendente");
      return;
    }
    setSyncing(true);
    const { ok, fail, skipped } = await flushQueue((ep, payload) => api.post(ep, payload));
    setSyncing(false);
    await refresh();
    if (skipped) return;
    if (ok > 0) toast.success(`${ok} apontamento${ok > 1 ? "s" : ""} enviado${ok > 1 ? "s" : ""}`);
    if (fail > 0) toast.error(`${fail} apontamento${fail > 1 ? "s" : ""} pendente${fail > 1 ? "s" : ""} — sem sinal?`);
  }, [refresh]);

  useEffect(() => {
    refresh();
    const onOnline = () => { setOnline(true); sync(true); setTimeout(autoPreloadIfStale, 8000); };
    const onOffline = () => setOnline(false);
    const onChange = () => refresh();
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("offline-queue-changed", onChange);
    // Tenta sincronizar ao montar (caso app foi aberto com fila pendente) e pré-carrega dados p/ offline
    if (navigator.onLine) { sync(true); setTimeout(autoPreloadIfStale, 8000); }
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("offline-queue-changed", onChange);
    };
  }, [refresh, sync]);

  if (pending === 0 && online) return null;

  return (
    <div
      data-testid="offline-sync-bar"
      onClick={() => navigate("/fila-offline")}
      style={{
        position: "fixed", top: 12, right: 12, zIndex: 90,
        background: !online ? "#DC2626" : "#0A0A0A",
        color: !online ? "#FFF" : "#FFCC00",
        padding: "10px 14px", borderRadius: 6,
        display: "flex", alignItems: "center", gap: 10,
        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
        fontSize: "0.82rem", fontWeight: 600,
        maxWidth: 320, cursor: "pointer",
      }}
    >
      {!online ? <WifiSlash size={18} weight="bold" /> : (syncing ? <CloudArrowUp size={18} weight="bold" /> : <CheckCircle size={18} weight="bold" />)}
      <div style={{ flex: 1, lineHeight: 1.2 }}>
        {!online && <div>Sem internet</div>}
        {pending > 0 && (
          <div>
            {pending} aguardando envio
            {online && !syncing && (
              <button
                onClick={(e) => { e.stopPropagation(); sync(false); }}
                data-testid="btn-sync-now"
                style={{ marginLeft: 8, background: "#FFCC00", color: "#0A0A0A", border: 0, padding: "3px 8px", borderRadius: 3, cursor: "pointer", fontWeight: 700, fontSize: "0.72rem" }}
              >Enviar agora</button>
            )}
            {syncing && <span style={{ marginLeft: 8, opacity: 0.85 }}>enviando…</span>}
          </div>
        )}
      </div>
    </div>
  );
}
